import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Card, Spinner, Text, tokens } from "@fluentui/react-components";
import { api } from "../lib/api.ts";

interface AiWindow {
  calls: number;
  failures: number;
  promptTokens: number;
  completionTokens: number;
  avgMs: number;
  costUsd: number;
}
interface WaWindow {
  outbound: number;
  inbound: number;
  estConversations: number;
  estCostUsd: number;
}
interface Ops {
  generatedAt: string;
  ai: { enabled: boolean; endpoint: string | null; deployment: string; pricesPerM: { input: number; output: number }; last7: AiWindow; last30: AiWindow; allTime: AiWindow };
  whatsapp: { enabled: boolean; pricePerConversation: number; last7: WaWindow; last30: WaWindow };
  cycle: { schedules: Record<string, number>; checkIns: Record<string, number>; sessionsPlaced: number; unplaced: number };
  growth: { newTrainers30: number; newClients30: number; optedOutClients: number };
  azure: { available: boolean; reason?: string; currency?: string; monthToDate?: number; byService?: { service: string; cost: number }[]; scope?: string };
}

const usd = (n: number) => `$${n.toFixed(n < 1 ? 4 : 2)}`;
const money = (n: number, cur = "GBP") => new Intl.NumberFormat("en-GB", { style: "currency", currency: cur }).format(n);
const k = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

function Stat({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div style={{ minWidth: 120 }}>
      <Text size={200} block style={{ color: tokens.colorNeutralForeground3 }}>
        {label}
      </Text>
      <Text size={500} weight="semibold" block>
        {value}
      </Text>
      {sub && (
        <Text size={200} block style={{ color: tokens.colorNeutralForeground3 }}>
          {sub}
        </Text>
      )}
    </div>
  );
}

function Section({ title, right, children }: { title: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <Card style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Text weight="semibold" size={400}>
          {title}
        </Text>
        {right}
      </div>
      {children}
    </Card>
  );
}

const Row = ({ children }: { children: React.ReactNode }) => <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>{children}</div>;

export function OpsPanel() {
  const q = useQuery({ queryKey: ["admin", "ops"], queryFn: () => api.get<Ops>("/api/admin/ops"), refetchInterval: 5 * 60_000 });
  const aiPing = useMutation({ mutationFn: () => api.post<{ ok: boolean; status: number; ms?: number; detail: string }>("/api/admin/ai/ping") });

  if (q.isLoading) return <Spinner label="Loading operations…" />;
  if (!q.data) return <Text>Couldn't load operations data.</Text>;
  const o = q.data;
  const sched = o.cycle.schedules;
  const ci = o.cycle.checkIns;
  const totalCi = Object.values(ci).reduce((a, b) => a + b, 0);
  const estMonthly = o.ai.last30.costUsd + o.whatsapp.last30.estCostUsd;

  return (
    <div style={{ display: "grid", gap: 16, marginBottom: 24 }}>
      <Section
        title="Cost of operation"
        right={
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            Updated {new Date(o.generatedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
          </Text>
        }
      >
        <Row>
          <Stat
            label="Azure (month to date)"
            value={o.azure.available ? money(o.azure.monthToDate ?? 0, o.azure.currency) : "—"}
            sub={o.azure.available ? `${o.azure.byService?.length ?? 0} services` : o.azure.reason}
          />
          <Stat label="AI, last 30 days" value={usd(o.ai.last30.costUsd)} sub={`${o.ai.last30.calls} calls`} />
          <Stat label="WhatsApp, last 30 days" value={usd(o.whatsapp.last30.estCostUsd)} sub={`~${o.whatsapp.last30.estConversations} conversations (est.)`} />
          <Stat label="Usage run-rate / month" value={usd(estMonthly)} sub="AI + WhatsApp, excl. Azure" />
        </Row>
        {o.azure.available && o.azure.byService && o.azure.byService.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 4 }}>
            {o.azure.byService.map((s) => (
              <Text key={s.service} size={200} style={{ color: tokens.colorNeutralForeground2 }}>
                {s.service}: {money(s.cost, o.azure.currency)}
              </Text>
            ))}
          </div>
        )}
      </Section>

      <Section
        title="AI model"
        right={
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            {aiPing.data && (
              <Text size={200} style={{ color: aiPing.data.ok ? tokens.colorPaletteGreenForeground1 : tokens.colorPaletteRedForeground1 }}>
                {aiPing.data.ok ? `OK — ${aiPing.data.ms} ms` : `Failed (${aiPing.data.status}): ${aiPing.data.detail}`}
              </Text>
            )}
            <Button size="small" onClick={() => aiPing.mutate()} disabled={aiPing.isPending}>
              {aiPing.isPending ? "Testing…" : "Test connection"}
            </Button>
          </div>
        }
      >
        <Row>
          <Stat label="Deployment" value={o.ai.deployment} sub={o.ai.enabled ? (o.ai.endpoint ?? "") : "AI disabled — rule-based fallback"} />
          <Stat label="Price / 1M tokens" value={`${usd(o.ai.pricesPerM.input)} in · ${usd(o.ai.pricesPerM.output)} out`} sub="AI_PRICE_*_PER_M" />
        </Row>
        <Row>
          {(["last7", "last30", "allTime"] as const).map((w) => {
            const a = o.ai[w];
            return (
              <Stat
                key={w}
                label={w === "last7" ? "Last 7 days" : w === "last30" ? "Last 30 days" : "All time"}
                value={usd(a.costUsd)}
                sub={`${a.calls} calls · ${k(a.promptTokens)} in / ${k(a.completionTokens)} out · ${a.avgMs} ms avg${a.failures ? ` · ${a.failures} failed` : ""}`}
              />
            );
          })}
        </Row>
      </Section>

      <Section title="This week's cycle (all trainers)">
        <Row>
          <Stat label="Planning" value={sched.PLANNING ?? 0} />
          <Stat label="Collecting replies" value={sched.COLLECTING ?? 0} />
          <Stat label="Awaiting review" value={sched.PROPOSED ?? 0} />
          <Stat label="Published" value={sched.PUBLISHED ?? 0} />
          <Stat label="Holiday" value={sched.HOLIDAY ?? 0} />
        </Row>
        <Row>
          <Stat label="Check-ins" value={totalCi} sub={`${ci.COMPLETED ?? 0} replied · ${ci.PENDING ?? 0} waiting · ${ci.IN_PROGRESS ?? 0} in progress · ${ci.EXPIRED ?? 0} expired`} />
          <Stat label="Sessions placed" value={o.cycle.sessionsPlaced} />
          <Stat label="Clients unplaced" value={o.cycle.unplaced} />
        </Row>
      </Section>

      <Section title="Messaging & growth">
        <Row>
          <Stat label="WhatsApp" value={o.whatsapp.enabled ? "Live" : "Simulated"} sub={`${o.whatsapp.last7.outbound} out / ${o.whatsapp.last7.inbound} in, last 7 days`} />
          <Stat label="New trainers (30d)" value={o.growth.newTrainers30} />
          <Stat label="New clients (30d)" value={o.growth.newClients30} />
          <Stat label="Clients opted out" value={o.growth.optedOutClients} />
        </Row>
      </Section>
    </div>
  );
}
